import { renderTemplateContent } from "../contentRenderer";
import { buttonClass, mergeTemplateDesign, sectionPaddingClass, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { SiteFooter, SiteHeader } from "../templateParts";
import type { SiteTemplateDefinition, TemplateRenderProps } from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { BoutiqueMobileMenu } from "./BoutiqueMobileMenu";

function BoutiqueServiceStyles() {
    return (
        <style>{`
            [data-web-template="boutique-service"] {
                --boutique-gold: #e4b779;
                --boutique-ink: #2b1b1f;
                --boutique-ivory: #fffaf2;
                --boutique-wine-glass: rgba(55, 8, 27, .96);
                container-type: inline-size;
                overflow-x: hidden;
                scroll-behavior: smooth;
                scroll-padding-top: 74px;
            }

            [data-web-template="boutique-service"] .site-builder-section {
                padding-inline: clamp(1rem, 3vw, 2.5rem);
                position: relative;
            }

            [data-web-template="boutique-service"] .site-builder-grid {
                display: grid;
                gap: clamp(1rem, 2.5vw, 2.35rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="boutique-service"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="boutique-service"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="boutique-service"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.15rem;
                min-width: 0;
            }

            [data-web-template="boutique-service"] .site-builder-column-inline,
            [data-web-template="boutique-service"] [data-document-role="headerActions"] {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="boutique-service"] [data-web-region="header"] {
                background: #310d1d !important;
                border-color: transparent !important;
                left: 0;
                min-height: 108px;
                padding-inline: clamp(1rem, 3vw, 2.5rem);
                position: fixed !important;
                right: 0;
                top: 0;
                transition: background 320ms ease, border-color 320ms ease, box-shadow 320ms ease, min-height 320ms ease;
                z-index: 40;
            }

            [data-web-template="boutique-service"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="boutique-service"] [data-web-region="header"] :where(
                [data-document-role="brand"] .site-builder-text,
                [data-document-role="brand"] .site-builder-text > span,
                [data-document-role="brandCopy"] .site-builder-text,
                [data-document-role="brandCopy"] .site-builder-text > span,
                [data-document-role="pageNavigation"] .site-builder-text,
                [data-document-role="pageNavigation"] .site-builder-text > span,
                .site-navigation-primary-link,
                .site-navigation-primary-trigger
            ) {
                color: var(--boutique-ivory) !important;
            }

            [data-web-template="boutique-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text {
                color: var(--boutique-ivory) !important;
            }

            [data-web-template="boutique-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text > span {
                color: var(--boutique-ivory) !important;
            }

            [data-web-template="boutique-service"] [data-web-region="header"]:not([data-boutique-header-scrolled]) .site-navigation-marker-cinematic {
                color: var(--boutique-gold) !important;
            }

            [data-web-template="boutique-service"] [data-web-region="header"] [data-document-role="brandCopy"] .site-builder-text:last-child {
                opacity: .62;
            }

            [data-web-template="boutique-service"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-boutique-header-scrolled] {
                backdrop-filter: blur(20px) saturate(120%);
                background: rgba(251, 247, 242, .94) !important;
                border-color: rgba(43, 27, 31, .14) !important;
                box-shadow: 0 16px 52px rgba(43, 27, 31, .1);
                color: var(--site-text);
                min-height: 70px;
            }

            [data-web-template="boutique-service"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-boutique-header-scrolled] :where(
                [data-document-role="brand"] .site-builder-text,
                [data-document-role="brand"] .site-builder-text > span,
                [data-document-role="brandCopy"] .site-builder-text,
                [data-document-role="brandCopy"] .site-builder-text > span,
                [data-document-role="pageNavigation"] .site-builder-text,
                [data-document-role="pageNavigation"] .site-builder-text > span,
                .site-navigation-primary-link,
                .site-navigation-primary-trigger
            ) {
                color: var(--site-text) !important;
            }

            [data-web-template="boutique-service"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-boutique-header-scrolled] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text {
                color: var(--site-text) !important;
            }

            [data-web-template="boutique-service"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-boutique-header-scrolled] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text > span {
                color: var(--site-text) !important;
            }

            [data-web-template="boutique-service"] [data-document-role="pageNavigation"] {
                align-items: center;
                display: flex;
                gap: clamp(1rem, 2.2vw, 2.15rem);
                justify-content: center;
            }

            [data-web-template="boutique-service"] [data-document-role="pageNavigation"] .site-builder-text {
                font-size: .72rem !important;
                font-weight: 760 !important;
                letter-spacing: .11em !important;
                position: relative;
                text-transform: uppercase;
                width: auto !important;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] {
                background: var(--site-primary) !important;
                min-height: 100svh;
                overflow: hidden;
                padding: 0;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"]::after {
                background:
                    linear-gradient(90deg, rgba(34, 9, 18, .17) 26%, rgba(34, 9, 18, .66) 60%, rgba(34, 9, 18, .9) 100%),
                    linear-gradient(0deg, rgba(22, 6, 12, .44), transparent 56%);
                content: "";
                inset: 0;
                pointer-events: none;
                position: absolute;
                z-index: 1;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] > .site-builder-grid {
                min-height: 100svh;
                position: relative;
                width: 100%;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroVisual"] {
                grid-column: 1 / -1 !important;
                grid-row: 1 / -1 !important;
                inset: 0;
                min-height: 100svh;
                position: absolute;
                width: 100%;
                z-index: 0;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroVisual"] .site-builder-image {
                filter: saturate(.72) contrast(1.04) brightness(.82);
                height: 100svh !important;
                min-height: 100svh !important;
                object-position: center 34% !important;
                transform: scale(1.045);
                transition: filter 900ms ease, transform 7.6s cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="boutique-service"][data-boutique-runtime-ready] [data-web-region="hero"] [data-document-role="heroVisual"] .site-builder-image {
                filter: saturate(.86) contrast(1.03) brightness(.84);
                transform: scale(1);
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroCopy"] {
                align-content: end;
                grid-column: 6 / -1 !important;
                grid-row: 1 / -1 !important;
                min-height: 100svh;
                padding: clamp(9rem, 18vh, 13rem) clamp(1rem, 3vw, 3.5rem) clamp(4rem, 9vh, 7rem) 0;
                position: relative;
                z-index: 2;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] .site-builder-text-h1 {
                color: var(--boutique-ivory) !important;
                font-size: clamp(3.8rem, 5.25vw, 5.55rem) !important;
                hyphens: none;
                line-height: .88 !important;
                max-width: 9.25ch;
                overflow-wrap: anywhere !important;
                text-shadow: 0 5px 34px rgba(22, 6, 12, .3);
                text-wrap: balance;
                word-break: normal;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroCopy"] :where(.site-builder-text, .site-builder-text-paragraph) {
                color: var(--boutique-ivory) !important;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroCopy"] > .site-builder-blockquote {
                color: var(--boutique-ivory) !important;
            }

            [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroCopy"] .site-builder-card :where(
                .site-builder-card-title,
                .site-builder-card-text,
                .site-builder-text,
                .site-builder-text-paragraph
            ) {
                color: var(--site-text) !important;
            }

            [data-web-template="boutique-service"] .site-builder-form :where(input, textarea)::placeholder {
                color: var(--site-muted);
                opacity: 1;
            }

            [data-web-template="boutique-service"] [data-web-builder-region="page"] > .site-builder-section:not([data-web-region="hero"]) {
                padding-block: clamp(5.5rem, 10vw, 10rem);
            }

            [data-web-template="boutique-service"] [data-web-builder-region="page"] > .site-builder-section:nth-child(odd):not([data-web-region="hero"]) {
                background: color-mix(in srgb, var(--site-primary) 6%, var(--site-background)) !important;
            }

            [data-web-template="boutique-service"] .site-builder-card {
                backdrop-filter: blur(12px);
                box-shadow: 0 18px 58px rgba(70, 18, 37, .07);
                overflow: hidden;
                transition: border-color 280ms ease, box-shadow 420ms ease, transform 420ms cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="boutique-service"] :where([data-document-role="editorialVisual"], [data-document-role="galleryTile"]) {
                overflow: hidden;
            }

            [data-web-template="boutique-service"] :where([data-document-role="editorialVisual"], [data-document-role="galleryTile"]) .site-builder-image,
            [data-web-template="boutique-service"] [data-web-builder-region="page"] > .site-builder-section:not([data-web-region="hero"]) .site-builder-image {
                filter: saturate(.72) contrast(1.03);
                transition: filter 650ms ease, transform 900ms cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="boutique-service"] [data-web-region="bookingCta"],
            [data-web-template="boutique-service"] [data-web-builder-region="page"] > .site-builder-section:last-child {
                overflow: hidden;
                position: relative;
            }

            [data-web-template="boutique-service"] [data-web-region="bookingCta"]::after,
            [data-web-template="boutique-service"] [data-web-builder-region="page"] > .site-builder-section:last-child::after {
                border: 1px solid color-mix(in srgb, var(--site-accent) 42%, transparent);
                border-radius: 50%;
                content: "";
                height: min(50vw, 640px);
                pointer-events: none;
                position: absolute;
                right: -18vw;
                top: -70%;
                width: min(50vw, 640px);
            }

            [data-web-template="boutique-service"] [data-web-region="footer"] {
                border-top: 1px solid color-mix(in srgb, var(--site-accent) 42%, transparent);
            }

            [data-web-template="boutique-service"] [data-web-region="footer"] [data-document-role="footerNavigation"] {
                align-content: start;
            }

            [data-web-template="boutique-service"]:not([data-web-template-editor="true"])[data-boutique-runtime-ready] [data-boutique-reveal] {
                filter: blur(3px);
                opacity: 0;
                transform: translate3d(0, 20px, 0) scale(.992);
                transition: filter 560ms ease, opacity 560ms ease, transform 680ms cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="boutique-service"]:not([data-web-template-editor="true"])[data-boutique-runtime-ready] [data-boutique-reveal][data-boutique-revealed] {
                filter: blur(0);
                opacity: 1;
                transform: translate3d(0, 0, 0) scale(1);
            }

            [data-web-template="boutique-service"] .boutique-mobile-menu-toggle {
                display: none;
            }

            @media (hover: hover) and (pointer: fine) {
                [data-web-template="boutique-service"] .site-builder-card:hover {
                    border-color: color-mix(in srgb, var(--site-accent) 68%, var(--site-border));
                    box-shadow: 0 28px 78px rgba(70, 18, 37, .16);
                    transform: translateY(-5px);
                }

                [data-web-template="boutique-service"] [data-web-builder-region="page"] > .site-builder-section:not([data-web-region="hero"]) .site-builder-image:hover {
                    filter: saturate(.96) contrast(1.04);
                    transform: scale(1.025);
                }
            }

            @container (max-width: 900px) {
                [data-web-template="boutique-service"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="boutique-service"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"] {
                    min-height: 72px;
                    padding-inline: 1rem;
                }

                [data-web-template="boutique-service"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="pageNavigation"]),
                [data-web-template="boutique-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="boutique-service"] [data-web-region="header"] .boutique-mobile-menu-toggle {
                    align-items: center;
                    background: rgba(31, 10, 19, .2);
                    border: 1px solid currentColor;
                    color: var(--boutique-ivory);
                    cursor: pointer;
                    display: grid;
                    gap: 5px;
                    height: 44px;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 4;
                }

                [data-web-template="boutique-service"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-boutique-header-scrolled] .boutique-mobile-menu-toggle {
                    color: var(--site-text);
                }

                [data-web-template="boutique-service"] .boutique-mobile-menu-toggle > span {
                    background: currentColor;
                    display: block;
                    height: 1px;
                    transition: opacity 180ms ease, transform 180ms ease;
                    width: 23px;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] {
                    background: #37081b !important;
                    bottom: 0;
                    color: var(--boutique-ivory);
                    left: 0;
                    min-height: 100svh;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] :is(
                    [data-document-role="brand"] .site-builder-text,
                    [data-document-role="brand"] .site-builder-text > span,
                    [data-document-role="brandCopy"] .site-builder-text,
                    [data-document-role="brandCopy"] .site-builder-text > span,
                    [data-document-role="pageNavigation"] .site-builder-text,
                    [data-document-role="pageNavigation"] .site-builder-text > span
                ) {
                    color: var(--boutique-ivory) !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-header-scrolled][data-boutique-mobile-menu-open] :is(
                    [data-document-role="brand"] .site-builder-text,
                    [data-document-role="brand"] .site-builder-text > span,
                    [data-document-role="brandCopy"] .site-builder-text,
                    [data-document-role="brandCopy"] .site-builder-text > span
                ) {
                    color: var(--boutique-ivory) !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-header-scrolled][data-boutique-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:first-child [data-document-role="brandCopy"] .site-builder-text,
                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-header-scrolled][data-boutique-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:first-child [data-document-role="brandCopy"] .site-builder-text > span {
                    color: var(--boutique-ivory) !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] [data-site-navigation-root="true"] {
                    --site-navigation-accent: var(--boutique-gold) !important;
                    --site-navigation-background: var(--site-primary) !important;
                    --site-navigation-border: rgba(255, 250, 242, .2) !important;
                    --site-navigation-muted: rgba(255, 250, 242, .76) !important;
                    --site-navigation-surface: rgba(255, 250, 242, .08) !important;
                    --site-navigation-text: var(--boutique-ivory) !important;
                    color: var(--boutique-ivory) !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] .boutique-mobile-menu-toggle {
                    color: var(--boutique-ivory);
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="pageNavigation"]),
                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="pageNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(4rem, 12vh, 7rem);
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] [data-document-role="pageNavigation"] {
                    align-items: stretch;
                    display: flex;
                    flex-direction: column;
                    gap: 0;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] [data-document-role="pageNavigation"] > * {
                    border-bottom: 1px solid rgba(255, 250, 242, .18);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] [data-document-role="pageNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2.65rem, 12vw, 4.5rem) !important;
                    font-weight: 520 !important;
                    letter-spacing: -.045em !important;
                    text-transform: none;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    border-top: 1px solid rgba(255, 250, 242, .18);
                    display: flex;
                    flex-direction: column;
                    gap: .75rem;
                    margin-top: 1rem;
                    padding-top: 1rem;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] [data-document-role="headerActions"] > * {
                    width: 100% !important;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] .boutique-mobile-menu-toggle > span:first-child {
                    transform: translateY(6px) rotate(45deg);
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] .boutique-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="boutique-service"] [data-web-region="header"][data-boutique-mobile-menu-open] .boutique-mobile-menu-toggle > span:last-child {
                    transform: translateY(-6px) rotate(-45deg);
                }

                [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroVisual"] {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                    position: absolute;
                }

                [data-web-template="boutique-service"] [data-web-region="hero"] [data-document-role="heroCopy"] {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                    padding: 8.75rem 1rem 4rem;
                }

                [data-web-template="boutique-service"] [data-web-region="hero"]::after {
                    background: linear-gradient(0deg, rgba(34, 9, 18, .94) 7%, rgba(34, 9, 18, .43) 68%, rgba(34, 9, 18, .45));
                }

                [data-web-template="boutique-service"] [data-web-region="hero"] .site-builder-text-h1 {
                    font-size: clamp(3.15rem, 13vw, 4.8rem) !important;
                    max-width: 100% !important;
                    overflow-wrap: anywhere !important;
                    white-space: normal !important;
                    width: 100% !important;
                }
            }

            @container (max-width: 640px) {
                [data-web-template="boutique-service"] .site-builder-section {
                    padding-inline: 1rem;
                }

                [data-web-template="boutique-service"] [data-web-builder-region="page"] > .site-builder-section:not([data-web-region="hero"]) {
                    padding-block: 5rem;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="boutique-service"] *,
                [data-web-template="boutique-service"] *::before,
                [data-web-template="boutique-service"] *::after {
                    animation-duration: .01ms !important;
                    animation-iteration-count: 1 !important;
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="boutique-service"] [data-boutique-reveal] {
                    filter: none !important;
                    opacity: 1 !important;
                    transform: none !important;
                }
            }
        `}</style>
    );
}

export const boutiqueServiceTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "boutique-service",
        name: "Prémiové služby a osobní značka",
        type: "website",
        category: "services",
        description: "Elegantní boutique šablona pro prémiové služby, osobní značku, beauty studio nebo privátní poradenský koncept.",
        previewImage: "/templates/boutique-service/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["boutique-service"],
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["classic", "centered", "minimal"],
            footer: ["columns", "contact", "simple"],
            buttons: ["solid", "pill", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(boutiqueServiceTemplate.manifest.defaults, props.designOverride ?? props.site?.templateDesign);
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                className="min-h-screen max-w-full bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                data-web-template="boutique-service"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <BoutiqueServiceStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    fallback={<SiteHeader site={props.site} pages={props.pages} design={design} LinkComponent={LinkComponent} headerActions={props.headerActions} />}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <BoutiqueMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={<main>
                    <section className={`${sectionPaddingClass(design.layout.sectionSpacing)} px-5 sm:px-6`}>
                        <div className="mx-auto max-w-[var(--site-container)] text-center">
                            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--site-primary)]">Sluzby a prezentace</p>
                            <h1 className="mx-auto mt-5 max-w-4xl font-[var(--site-heading-font)] text-4xl font-bold leading-tight sm:text-6xl">
                                {props.page?.title ?? `${props.site?.name ?? "Studio"} s osobnim pristupem`}
                            </h1>
                            <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-[var(--site-muted)]">
                                {props.page
                                    ? "Stranka pouziva jemny editorialni layout a nastaveni barev z administrace."
                                    : "Idealni sablona pro poradce, salony, studia a lokalni firmy, ktere potrebuji pusobit osobne a duveryhodne."}
                            </p>
                            <div className="mt-8 flex justify-center">
                                <LinkComponent href="/kontakt" className={`px-6 py-3 text-sm font-semibold ${buttonClass(design.components.buttons)}`}>
                                    Domluvit konzultaci
                                </LinkComponent>
                            </div>
                        </div>
                    </section>
                    {props.page?.imageSlider}
                    {props.page ? (
                        <section className="px-5 pb-16 sm:px-6">
                            <article className="mx-auto max-w-3xl rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 text-lg leading-8 text-[var(--site-muted)] shadow-sm sm:p-10 [&_a]:text-[var(--site-primary)] [&_h1]:mb-5 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-3xl [&_h1]:font-bold [&_h2]:mb-4 [&_h2]:mt-8 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-2xl [&_h2]:font-bold [&_p]:mb-5">
                                {renderTemplateContent(props.page, design, LinkComponent, props.leadForm, props.readOnly)}
                            </article>
                        </section>
                    ) : (
                        <section className="px-5 pb-16 sm:px-6">
                            <div className="mx-auto grid max-w-[var(--site-container)] gap-5 md:grid-cols-3">
                                {["Pece", "Proces", "Vysledek"].map((item) => (
                                    <article key={item} className="rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 text-left shadow-sm">
                                        <p className="font-[var(--site-heading-font)] text-xl font-bold">{item}</p>
                                        <p className="mt-3 text-sm leading-6 text-[var(--site-muted)]">
                                            Predpripraveny blok, ktery se da dal napojit na obsahove bloky web editoru.
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </section>
                    )}
                    </main>}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    fallback={<SiteFooter site={props.site} pages={props.pages} design={design} LinkComponent={LinkComponent} />}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
